import { StorageKeys } from '../../shared/models/storage-keys';
import { Injectable } from '@angular/core';
import { LocalStorageService } from '../services/local-storage.service';
import { BrowserHelper } from './browser.helper';
import { LocationHelper } from './location.helper';
import { Observable, throwError } from 'rxjs';
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from '@angular/common/http';
import { MobileWebViewService } from '../services/mobile-web-view.service';
import { catchError, tap } from 'rxjs/operators';
import { ModalOptionsConfig, ModalService } from '../../lean-shared/components/nz-modal-service/modal.service';
import { DEFAULT_MODAL_KAMONA_CLASSES, DEFAULT_MODAL_WIDTH, MONIEPOINT_DESIGN_TYPE } from '../../shared/constants';
import { Router } from '@angular/router';
import { NzModalRef } from 'ng-zorro-antd/modal';
import { DataLayerService } from '../services/data-layer-service.service';
import 'clientjs';
import { initializeDatalayer } from '../../shared/models/data.layer.model';
import { AppConfig } from '../../shared/config/app-config';
import UserAgentBuilder from '../../utilities/user-agent-builder/user-agent-builder';
import { ErrorHandlerService } from '../services/utilities/error-handler.service';

@Injectable()
export class ApiClientHelper {
    headers: HttpHeaders;
    requestOptions: RequestOptions;
    expirationSeconds = this.localStorage.get(StorageKeys.expires_in);
    currentDateTime;
    sessionExpirationTime;
    runningModal: NzModalRef;
    readonly logoutMessage = 'You have been on this process for too long. For security reasons, we need to log you out. Log back in to continue.';
    userAgent: string;

    constructor(
        public http: HttpClient,
        public localStorage: LocalStorageService,
        public browserHelper: BrowserHelper,
        public router: Router,
        public mobileWebViewService: MobileWebViewService,
        public modalService: ModalService,
        public dataLayerService: DataLayerService,
        public config: AppConfig,
        public locationHelper: LocationHelper,
        private errorHandler: ErrorHandlerService,
    ) {
        const userAgentBuilder = new UserAgentBuilder();

        this.userAgent = userAgentBuilder
            .setPart('appName', 'Moniepoint Frontoffice UI Angular')
            .setPart('appVersion', '3.0.0')
            .setPart('platformDetails', window.navigator.userAgent)
            .setPart('buildNumber', this.config['xUserAgent'])
            .build();
    }

    checkForUnauthorizedRequest(isExpired = false) {
        isExpired = isExpired || this.browserHelper.isTokenExpired();
        if (isExpired) {
            this.logExpiredTokenEvent('token_expired');
            this.logExpiredTokenEvent('Token Expired');
            this.logout();
        }
    }

    isTokenExpired() {
        return this.browserHelper.isTokenExpired();
    }

    handleExpiredTokenLogout() {
        let tokenExpired = sessionStorage.getItem('expired_token');
        tokenExpired = tokenExpired ? JSON.parse(tokenExpired) : false;
        if (!tokenExpired) {
            return;
        }
        this.launchLogoutAlert();
        sessionStorage.removeItem('expired_token');
    }

    launchLogoutAlert() {
        const config = new ModalOptionsConfig();
        config.nzComponentParams.alertType = 'error';
        config.nzComponentParams.title = 'Session Expired';
        config.className = DEFAULT_MODAL_KAMONA_CLASSES;
        config.nzWidth = DEFAULT_MODAL_WIDTH;
        config.nzComponentParams.designType = MONIEPOINT_DESIGN_TYPE.KAMONA;
        config.nzComponentParams.callbackName = 'Continue';
        config.nzComponentParams.message = this.logoutMessage;
        this.runningModal = this.modalService.alert(config);
    }

    getDeviceId() {
        const isMobileUser = this.mobileWebViewService.isMobileWebViewUser();
        if (isMobileUser) {
            return this.mobileWebViewService?.getMoniepointAppParameters()?.deviceRegistrationRequest?.deviceUniqueIdentifier;
        }
        // @ts-ignore
        const client = new ClientJS();
        return client.getFingerprint().toString();
    }

    logExpiredTokenEvent(event) {
        const deviceId = `${this.getDeviceId()}`;
        const datalayer = initializeDatalayer(event);
        datalayer.device_id = deviceId;
        this.dataLayerService.logEvent(datalayer);
    }

    getHeaderParams(addAgent = true, loginToken?: any, addBusinessCode = false, customHeaders = {}, addClientType = false, addAuthToken = true) {
        let token = this.localStorage.get(StorageKeys.token);
        if (loginToken) {
            token = loginToken;
        }

        let agentId = this.localStorage.get(StorageKeys.moniepointAgentId);
        if (agentId === 'undefined') {
            agentId = '';
        }

        let headerParams: { [key: string]: string } = {
            'Content-Type': 'application/json' }

        if(addAuthToken) {
            headerParams['Authorization'] = token ? `Bearer ${token}` : '';
        }
        

        if (agentId && addAgent) {
            // tslint:disable-next-line:no-string-literal
            headerParams['moniepointAgentId'] = agentId;
        }
        if (addClientType) {
            headerParams['client-type'] = this.localStorage.get('device_client_type') || 'WEB';
        }
        if (this.localStorage.get('agentDashboardData') && addBusinessCode) {
            headerParams.MNP_BUSINESS_CODE = JSON.parse(this.localStorage.get('agentDashboardData'))?.businessCode;
        }

        headerParams = { ...headerParams, ...customHeaders };
        return headerParams;
    }

    shouldStopApiCall(checkAuth): any {
        const stopApiCall = this.browserHelper.isTokenExpired() && checkAuth;
        if (!stopApiCall) {
            return;
        }
        this.checkForUnauthorizedRequest(true);
        this.logExpiredTokenEvent('Token Expired');
        throw new Error('User Token Expired');
        return new Promise((resolve) => {});
    }

    public getForPromise(
        url: string,
        checkAuth = true,
        addAgent = true,
        addBusinessCode = false,
        customHeaders = {},
        urlParams = {},
        addClientType = true,
        addAuthToken = true
    ): Promise<any> {
        this.shouldStopApiCall(checkAuth);
        const requestHeaders = new HttpHeaders(this.getHeaderParams(addAgent, null, addBusinessCode, customHeaders, addClientType, addAuthToken));
        let params = new HttpParams();
        Object.keys(urlParams).forEach((queryKey) => {
            params = params.append(queryKey, urlParams[queryKey]);
        });

        return new Promise((resolve, reject) => {
            this.http
                .get(url, { headers: requestHeaders, params })
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        return throwError(() => this.errorHandler.handleError(error));
                    }),
                )
                .subscribe(
                    (response) => {
                        resolve(response);
                    },
                    (error) => {
                        this.shouldStopApiCall(checkAuth);
                        reject(error);
                    },
                );
        });
    }

    public getForPromiseNoAuth(url: string, urlParams = {}): Promise<any> {
        let params = new HttpParams();
        Object.keys(urlParams).forEach((queryKey) => {
            params = params.append(queryKey, urlParams[queryKey]);
        });

        return new Promise((resolve, reject) => {
            this.http
                .get(url)
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        return throwError(() => this.errorHandler.handleError(error));
                    }),
                )
                .subscribe(
                    (response) => {
                        resolve(response);
                    },
                    (error) => {
                        reject(error);
                    },
                );
        });
    }

    public postForPromiseNoAuth(
        url: string,
        data: any,
        checkAuth = true,
        clientType = 'WEB',
        clientVersion = AppConfig.CLIENT_VERSION,
    ): Promise<any> {
        this.shouldStopApiCall(checkAuth);
        const savedClientType = this.localStorage.get('device_client_type');
        clientType = savedClientType || clientType;

        const headerParams: { [key: string]: string } = {
            'client-type': clientType,
            'client-version': clientVersion,
        };
        this.headers = new HttpHeaders(headerParams);

        return new Promise((resolve, reject) => {
            this.http
                .post(url, data, { headers: this.headers })
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        return throwError(() => this.errorHandler.handleError(error));
                    }),
                )
                .subscribe(
                    (response) => {
                        resolve(response);
                    },
                    (error) => {
                        this.shouldStopApiCall(checkAuth);
                        reject(error);
                    },
                );
        });
    }

    public getForObservable<T>(url: string, checkAuth = true, addAgent = true, addBusinessCode = false): Observable<T> {
        this.shouldStopApiCall(checkAuth);
        const requestHeaders = new HttpHeaders(this.getHeaderParams(addAgent, null, addBusinessCode));
        return this.http.get<T>(url, { headers: requestHeaders }).pipe(
            tap({
                error: () => {
                    this.shouldStopApiCall(checkAuth);
                },
            }),
        );
    }

    public getAssetFile(url: string): Promise<any> {
        const requestHeaders = new HttpHeaders({
            'Content-Type': 'application/json',
        });
        return new Promise((resolve, reject) => {
            this.http
                .get(url, { headers: requestHeaders, responseType: 'blob' })
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        return throwError(() => this.errorHandler.handleError(error));
                    }),
                )
                .subscribe(
                    (response) => {
                        const responseJson = response;
                        resolve(responseJson);
                    },
                    (error) => {
                        reject(error);
                    },
                );
        });
    }

    public getAsBlobForPromise(url: string): Promise<any> {
        this.shouldStopApiCall(true);
        const requestHeaders = new HttpHeaders(this.getHeaderParams());
        return new Promise((resolve, reject) => {
            this.http
                .get(url, { headers: requestHeaders, responseType: 'blob' })
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        return throwError(() => this.errorHandler.handleError(error));
                    }),
                )
                .subscribe(
                    (response) => {
                        const responseBlob = response;
                        resolve(responseBlob);
                    },
                    (error) => {
                        this.shouldStopApiCall(true);
                        reject(error);
                    },
                );
        });
    }

    public getAsBlobForObservable(url: string, addAgent = true, addBusinessCode = false): Observable<Blob> {
        const requestHeaders = new HttpHeaders(this.getHeaderParams(addAgent, null, addBusinessCode));
        return this.http.get(url, { headers: requestHeaders, responseType: 'blob', observe: 'body' }).pipe(
            tap({
                error: () => this.shouldStopApiCall(true),
            }),
        );
    }

    public postAsBlobForPromise(url: string, data: any): Promise<any> {
        this.buildRequestHeaders();
        return new Promise((resolve, reject) => {
            this.http
                .post(url, data, this.requestOptions)
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        return throwError(() => this.errorHandler.handleError(error));
                    }),
                )
                .subscribe(
                    (response) => {
                        const responseBlob = response;
                        resolve(responseBlob);
                    },
                    (error) => {
                        this.shouldStopApiCall(true);
                        reject(error);
                    },
                );
        });
    }

    public getAsObservable(url: string): Observable<any> {
        this.buildRequestHeaders();
        return this.http.get(url, this.requestOptions);
    }

    public postForPromise(
        url: string,
        data: any,
        checkAuth = true,
        addClientVersion = false,
        addAgent = true,
        clientType = 'WEB',
        customHeaders = {},
        addClienType = true,
        addAuthToken = true,
    ): Promise<any> {
        this.shouldStopApiCall(checkAuth);
        let requestHeaders = new HttpHeaders(this.getHeaderParams(addAgent, null, false, customHeaders, false, addAuthToken));
        const savedClientType = this.localStorage.get('device_client_type');
        clientType = savedClientType || clientType;
        if(addClienType) {
            requestHeaders = requestHeaders.append('client-type', `${clientType}`);
        }
        if (addClientVersion) {
            requestHeaders = requestHeaders.append('client-version', AppConfig.CLIENT_VERSION);
        }
        return new Promise((resolve, reject) => {
            this.http
                .post(url, data, { headers: requestHeaders })
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        return throwError(() => this.errorHandler.handleError(error));
                    }),
                )
                .subscribe(
                    (response) => {
                        const responseJson = response;
                        resolve(responseJson);
                    },
                    (error) => {
                        this.shouldStopApiCall(checkAuth);
                        reject(error);
                    },
                );
        });
    }

    public postForObservable<T>(
        url: string,
        data: any,
        checkAuth = true,
        addClientType = false,
        addAgent = true,
        clientType = 'WEB',
        addBusinessCode = false,
    ): Observable<T> {
        this.shouldStopApiCall(checkAuth);
        let requestHeaders = new HttpHeaders(this.getHeaderParams(addAgent, null, addBusinessCode));
        const savedClientType = this.localStorage.get('device_client_type');
        clientType = savedClientType || clientType;
        requestHeaders = requestHeaders.append('client-type', `${clientType}`);
        if (addClientType) {
            requestHeaders = requestHeaders.append('client-version', AppConfig.CLIENT_VERSION);
        }
        return this.http.post<T>(url, data, { headers: requestHeaders }).pipe(
            tap({
                error: () => {
                    this.shouldStopApiCall(checkAuth);
                },
            }),
        );
    }

    public putForObservable<T>(
        url: string,
        data: any,
        checkAuth = true,
        addClientType = false,
        addAgent = true,
        clientType = 'WEB',
        addBusinessCode = false,
    ): Observable<T> {
        this.shouldStopApiCall(checkAuth);
        let requestHeaders = new HttpHeaders(this.getHeaderParams(addAgent, null, addBusinessCode));
        const savedClientType = this.localStorage.get('device_client_type');
        clientType = savedClientType || clientType;
        requestHeaders = requestHeaders.append('client-type', `${clientType}`);
        if (addClientType) {
            requestHeaders = requestHeaders.append('client-version', AppConfig.CLIENT_VERSION);
        }
        return this.http.put<T>(url, data, { headers: requestHeaders }).pipe(
            tap({
                error: () => {
                    this.shouldStopApiCall(checkAuth);
                },
            }),
        );
    }

    public putForPromise(url: string, data: any, addClientType = false, addAgent = true, clientType = 'WEB', customHeaders = {}): Promise<any> {
        this.shouldStopApiCall(true);
        const savedClientType = this.localStorage.get('device_client_type');
        clientType = savedClientType || clientType;
        let requestHeaders = new HttpHeaders(this.getHeaderParams(addAgent, null, false, customHeaders));
        requestHeaders = requestHeaders.append('client-type', `${clientType}`);
        if (addClientType) {
            requestHeaders = requestHeaders.append('client-version', AppConfig.CLIENT_VERSION);
        }
        return new Promise((resolve, reject) => {
            this.http
                .put(url, data, { headers: requestHeaders })
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        return throwError(() => this.errorHandler.handleError(error));
                    }),
                )
                .subscribe(
                    (response) => {
                        const responseJson = response;
                        resolve(responseJson);
                    },
                    (error) => {
                        this.shouldStopApiCall(true);
                        reject(error);
                    },
                );
        });
    }
    public putForPromiseNoAuth(url: string, data: any): Promise<any> {
        this.shouldStopApiCall(true);
        return new Promise((resolve, reject) => {
            this.http
                .put(url, data)
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        return throwError(() => this.errorHandler.handleError(error));
                    }),
                )
                .subscribe(
                    (response) => {
                        const responseJson = response;
                        resolve(responseJson);
                    },
                    (error) => {
                        this.shouldStopApiCall(true);
                        reject(error);
                    },
                );
        });
    }

    public putForPromiseI(url: string, data: any): Promise<any> {
        this.shouldStopApiCall(true);
        const requestHeaders = new HttpHeaders(this.getHeaderParams());
        return new Promise((resolve, reject) => {
            this.http
                .put(url, data, { headers: requestHeaders })
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        return throwError(() => this.errorHandler.handleError(error));
                    }),
                )
                .subscribe(
                    (response) => {
                        const responseJson = response;
                        resolve(responseJson);
                    },
                    (error) => {
                        this.shouldStopApiCall(true);
                        reject(error);
                    },
                );
        });
    }

    public async loginPostForPromise(
        url,
        data,
        clientType = 'WEB',
        checkAuth = true,
        clientVersion = AppConfig.CLIENT_VERSION,
        addGeoHeaders = this.config.ENABLE_LOGIN_LOCATION_HEADERS,
    ): Promise<any> {
        this.shouldStopApiCall(checkAuth);
        const savedClientType = this.localStorage.get('device_client_type');
        clientType = savedClientType || clientType;

        const headerParams: { [key: string]: string } = {
            'Content-Type': 'application/json',
            'client-type': clientType,
            'client-version': clientVersion,
        };

        const hasPermission = await this.locationHelper.hasPermission();

        if (addGeoHeaders && hasPermission) {
            const { longitude, latitude } = await this.locationHelper.getCoordinates();
            headerParams['X-Longitude'] = String(longitude);
            headerParams['X-Latitude'] = String(latitude);
        }

        this.headers = new HttpHeaders(headerParams);

        return new Promise((resolve, reject) => {
            this.http
                .post(url, data, { headers: this.headers })
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        return throwError(() => this.errorHandler.handleError(error));
                    }),
                )
                .subscribe(
                    (response) => {
                        const responseJson = response;
                        resolve(responseJson);
                    },
                    (error) => {
                        this.shouldStopApiCall(checkAuth);
                        reject(error);
                    },
                );
        });
    }

    public loginUserTypeGetForPromise(url, params): Promise<any> {
        this.headers = new HttpHeaders({
            'Content-Type': 'application/json',
        });
        return new Promise((resolve, reject) => {
            this.http
                .get(url, { headers: this.headers, params })
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        return throwError(() => this.errorHandler.handleError(error));
                    }),
                )
                .subscribe(
                    (response) => {
                        const responseJson = response;
                        resolve(responseJson);
                    },
                    (error) => {
                        reject(error);
                    },
                );
        });
    }

    public newPostAsFormDataForPromise(url: string, params, checkAuth = true, addAgent = true): Promise<any> {
        this.shouldStopApiCall(checkAuth);
        return new Promise((resolve, reject) => {
            const payload = new FormData();
            Object.keys(params).forEach((k) => {
                payload.append(k, params[k]);
            });
            const token = this.localStorage.get(StorageKeys.token);
            const agentId = this.localStorage.get(StorageKeys.moniepointAgentId);
            const headerParams: { [key: string]: string } = {
                Authorization: token ? `Bearer ${token}` : '',
            };
            if (agentId && addAgent) {
                headerParams[`moniepointAgentId`] = agentId;
            }
            const requestHeaders = new HttpHeaders(headerParams);
            this.http
                .post(url, payload, { headers: requestHeaders })
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        return throwError(() => this.errorHandler.handleError(error));
                    }),
                )
                .subscribe(
                    (response) => {
                        const responseJson = response;
                        resolve(responseJson);
                    },
                    (error) => {
                        this.shouldStopApiCall(checkAuth);
                        reject(error);
                    },
                );
        });
    }

    public newPostAsFormDataForPromiseMultiFiles(url: string, params, checkAuth = true): Promise<any> {
        this.shouldStopApiCall(checkAuth);
        return new Promise((resolve, reject) => {
            const payload = new FormData();
            Object.keys(params).forEach((k) => {
                if (Array.isArray(params[k])) {
                    params[k].forEach((res) => {
                        payload.append(k, res);
                    });
                } else {
                    payload.append(k, params[k]);
                }
            });
            const token = this.localStorage.get(StorageKeys.token);
            const requestHeaders = new HttpHeaders({
                Authorization: token ? `Bearer ${token}` : '',
            });
            this.http
                .post(url, payload, { headers: requestHeaders })
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        return throwError(() => this.errorHandler.handleError(error));
                    }),
                )
                .subscribe(
                    (response) => {
                        const responseJson = response;
                        resolve(responseJson);
                    },
                    (error) => {
                        this.shouldStopApiCall(checkAuth);
                        reject(error);
                    },
                );
        });
    }

    public newPutAsFormDataForPromiseMultiFiles(url: string, params, checkAuth = true): Promise<any> {
        this.shouldStopApiCall(checkAuth);
        return new Promise((resolve, reject) => {
            const payload = new FormData();
            Object.keys(params).forEach((k) => {
                if (Array.isArray(params[k])) {
                    params[k].forEach((res) => {
                        payload.append(k, res);
                    });
                } else {
                    payload.append(k, params[k]);
                }
            });
            const token = this.localStorage.get(StorageKeys.token);
            const requestHeaders = new HttpHeaders({
                Authorization: token ? `Bearer ${token}` : '',
            });
            this.http
                .put(url, payload, { headers: requestHeaders })
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        return throwError(() => this.errorHandler.handleError(error));
                    }),
                )
                .subscribe(
                    (response) => {
                        const responseJson = response;
                        resolve(responseJson);
                    },
                    (error) => {
                        this.shouldStopApiCall(checkAuth);
                        reject(error);
                    },
                );
        });
    }

    public noAuthPostAsFormDataForPromise(url: string, params): Promise<any> {
        return new Promise((resolve, reject) => {
            const payload = new FormData();
            Object.keys(params).forEach((k) => {
                payload.append(k, params[k]);
            });
            const token = this.localStorage.get(StorageKeys.token);
            const requestHeaders = new HttpHeaders({
                Authorization: token ? `Bearer ${token}` : '',
            });
            this.http
                .post(url, payload, { headers: requestHeaders })
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        return throwError(() => this.errorHandler.handleError(error));
                    }),
                )
                .subscribe(
                    (response) => {
                        const responseJson = response;
                        resolve(responseJson);
                    },
                    (error) => {
                        reject(error);
                    },
                );
        });
    }

    public getImageFromKuwego(url: string, token): Promise<any> {
        return new Promise((resolve, reject) => {
            const requestHeaders = new HttpHeaders({
                Authorization: token ? `Bearer ${token}` : '',
            });
            this.http
                .get(url, { headers: requestHeaders, responseType: 'blob' })
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        return throwError(() => this.errorHandler.handleError(error));
                    }),
                )
                .subscribe(
                    (response) => {
                        resolve(response);
                    },
                    (error) => {
                        reject(error);
                    },
                );
        });
    }

    public newPutAsFormDataForPromise(url: string, params, addAgent = false): Promise<any> {
        this.shouldStopApiCall(true);
        return new Promise((resolve, reject) => {
            const payload = new FormData();
            Object.keys(params).forEach((k) => {
                payload.append(k, params[k]);
            });
            const token = this.localStorage.get(StorageKeys.token);
            const agentId = this.localStorage.get(StorageKeys.moniepointAgentId);
            const headerParams: { [key: string]: string } = {
                Authorization: `Bearer ${token}`,
            };
            if (agentId && addAgent) {
                headerParams[`moniepointAgentId`] = agentId;
            }
            headerParams['client-type'] = this.localStorage.get('device_client_type') || 'WEB';
            const requestHeaders = new HttpHeaders(headerParams);
            this.http
                .put(url, payload, { headers: requestHeaders })
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        return throwError(() => this.errorHandler.handleError(error));
                    }),
                )
                .subscribe(
                    (response) => {
                        const responseJson = response;
                        resolve(responseJson);
                    },
                    (error) => {
                        this.shouldStopApiCall(true);
                        reject(error);
                    },
                );
        });
    }

    public delete(url: string, addAgent = false, data?: any): Promise<any> {
        this.shouldStopApiCall(true);
        const requestHeaders = new HttpHeaders(this.getHeaderParams(addAgent));
        return new Promise((resolve, reject) => {
            this.http
                .delete(url, { headers: requestHeaders, body: data })
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        return throwError(() => this.errorHandler.handleError(error));
                    }),
                )
                .subscribe(
                    (response) => {
                        resolve(response);
                    },
                    (error) => {
                        this.shouldStopApiCall(true);
                        reject(error);
                    },
                );
        });
    }

    public deleteWithCustomHeader(url: string, addAgent = false, customHeaders = {}, data?: any): Promise<any> {
        this.shouldStopApiCall(true);
        const requestHeaders = new HttpHeaders(this.getHeaderParams(addAgent, null, null, customHeaders, false));
        return new Promise((resolve, reject) => {
            this.http
                .delete(url, { headers: requestHeaders, body: data })
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        return throwError(() => this.errorHandler.handleError(error));
                    }),
                )
                .subscribe(
                    (response) => {
                        resolve(response);
                    },
                    (error) => {
                        this.shouldStopApiCall(true);
                        reject(error);
                    },
                );
        });
    }

    public postAsFormDataForPromise(url: string, data: any): Promise<any> {
        this.shouldStopApiCall(true);
        return new Promise((resolve, reject) => {
            const xhr: XMLHttpRequest = new XMLHttpRequest();
            const token = this.localStorage.get('key');
            xhr.onreadystatechange = () => {
                if (xhr.readyState === 4) {
                    if (xhr.status === 200) {
                        resolve(JSON.parse(xhr.response));
                    } else {
                        this.shouldStopApiCall(true);
                        reject(JSON.parse(xhr.response));
                    }
                }
            };
            xhr.open('POST', url, true);
            if (token != null && token !== undefined) {
                xhr.setRequestHeader('Authorization', `Bearer ${token}`);
            }
            xhr.setRequestHeader('Content-Type', 'application/x-www-form-urlencoded');
            xhr.setRequestHeader('Accept', 'application/json, text/plain, */*');
            const formData = new FormData();
            if (data) {
                const dataKeys = Object.keys(data);
                dataKeys.forEach((key) => {
                    if (data[key] instanceof File) {
                        formData.append(key, data[key], data[key].name);
                    } else {
                        formData.append(key, data[key]);
                    }
                });
            }
            xhr.send(formData);
        });
    }

    public postForPromiseMultipart(
        url: string,
        data: any,
        checkAuth = true,
        addClientType = false,
        clientType = 'WEB',
        customHeaders = {},
    ): Promise<any> {
        this.shouldStopApiCall(checkAuth);
        const token = this.localStorage.get(StorageKeys.token);

        let requestHeaders = new HttpHeaders({
            Authorization: token ? `Bearer ${token}` : '',
            ...customHeaders,
        });
        const savedClientType = this.localStorage.get('device_client_type');
        clientType = savedClientType || clientType;
        requestHeaders = requestHeaders.append('client-type', `${clientType}`);
        if (addClientType) {
            requestHeaders = requestHeaders.append('client-version', AppConfig.CLIENT_VERSION);
        }

        return new Promise((resolve, reject) => {
            this.http
                .post(url, data, { headers: requestHeaders })
                .pipe(
                    catchError((error: HttpErrorResponse) => {
                        return throwError(() => this.errorHandler.handleError(error));
                    }),
                )
                .subscribe(
                    (response) => {
                        const responseJson = response;
                        resolve(responseJson);
                    },
                    (error) => {
                        this.shouldStopApiCall(checkAuth);
                        reject(error);
                    },
                );
        });
    }

    public putForPromiseMultipart(
        url: string,
        data: any,
        checkAuth = true,
        addClientType = false,
        clientType = 'WEB',
        customHeaders = {},
    ): Promise<any> {
        this.shouldStopApiCall(checkAuth);
        const token = this.localStorage.get(StorageKeys.token);

        let requestHeaders = new HttpHeaders({
            Authorization: token ? `Bearer ${token}` : '',
            ...customHeaders,
        });

        const savedClientType = this.localStorage.get('device_client_type');
        clientType = savedClientType || clientType;
        requestHeaders = requestHeaders.append('client-type', `${clientType}`);

        if (addClientType) {
            requestHeaders = requestHeaders.append('client-version', AppConfig.CLIENT_VERSION);
        }

        return new Promise((resolve, reject) => {
            this.http.put(url, data, { headers: requestHeaders }).subscribe(
                (response) => {
                    const responseJson = response;
                    resolve(responseJson);
                },
                (error) => {
                    this.shouldStopApiCall(checkAuth);
                    reject(error);
                },
            );
        });
    }

    public patchForPromise(
        url: string,
        data: any,
        checkAuth = true,
        addClientType = false,
        addAgent = true,
        clientType = 'WEB',
        customHeaders = {},
    ): Promise<any> {
        this.shouldStopApiCall(checkAuth);
        let requestHeaders = new HttpHeaders(this.getHeaderParams(addAgent, null, false, customHeaders));
        const savedClientType = this.localStorage.get('device_client_type');
        clientType = savedClientType || clientType;
        requestHeaders = requestHeaders.append('client-type', `${clientType}`);
        if (addClientType) {
            requestHeaders = requestHeaders.append('client-version', AppConfig.CLIENT_VERSION);
        }
        return new Promise((resolve, reject) => {
            this.http.patch(url, data, { headers: requestHeaders }).subscribe(
                (response) => {
                    const responseJson = response;
                    resolve(responseJson);
                },
                (error) => {
                    this.shouldStopApiCall(checkAuth);
                    reject(error);
                },
            );
        });
    }

    buildUrlQueryParams(data) {
        this.requestOptions.params = new HttpParams();
        if (data) {
            const dataKeys = Object.keys(data);
            const params = new HttpParams();
            dataKeys.forEach((key) => {
                params.append(data[key], data[key].name);
            });
            return params;
        }
    }

    private buildRequestHeaders(): void {
        const token = this.localStorage.get(StorageKeys.token);
        this.headers = new HttpHeaders({
            'Content-Type': 'application/json',
        });
        if (token != null && token !== undefined) {
            this.headers.append('Authorization', `Bearer ${token}`);
        }
        this.requestOptions = new RequestOptions();
        this.requestOptions.headers = this.headers;
        this.requestOptions.responseType = 'arraybuffer';
    }

    private logout() {
        this.localStorage.deleteAll();
        sessionStorage.setItem('expired_token', JSON.stringify(true));
        this.router.navigateByUrl(`/login`);
    }
}

class RequestOptions {
    headers?: HttpHeaders | { [header: string]: string | string[] };
    observe?: 'body';
    params?: HttpParams | { [param: string]: string | string[] };
    reportProgress?: boolean;
    responseType: 'arraybuffer';
    withCredentials?: boolean;
}
